import { useId, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@attendance/ui/components/IconButton';
import { cn } from '@attendance/ui/utils';
import { ArrowLeft, ChevronLeft, ShieldCheck, X } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore.js';
import { useShellSurface } from './shellSurface.js';
import { useBrandingStore, useFeatures } from '../../stores/brandingStore.js';
import { BrandLogo } from '../common/BrandLogo.js';
import { AppVersion } from './AppVersion.js';
import {
  NAV_ICON,
  RAIL_DIVIDER,
  RAIL_ICON_BUTTON,
  RAIL_INSET,
  RAIL_INSET_COLLAPSED,
} from './railSkin.js';
import { SurfaceSwitch } from './SidebarSurfaceSwitch.js';
import { Leaf, NavGroupSection, SectionHeading } from './NavRows.js';
import {
  PERSONAL,
  PERSONAL_SECTION,
  GROUPS,
  TOP_LEVEL,
  ADMIN_HOME,
  featureOn,
  isVisible,
  pathMatches,
} from './navItems.js';
import { NAV_COLLAPSED_KEY, NAV_EXPANDED_KEY, loadCollapsed, loadExpanded } from './navPrefs.js';

/* ===========================================================================
 * The rail. 252px of ink, against a warm-paper content column.
 *
 * A NEUTRAL rail against a light content column, with the brand reserved for
 * the one thing it should mark: where you are.
 *
 * This rail used to be painted in `--primary` — a full-height slab of the
 * brand. It was well built and its contrast was measured, but a saturated
 * surface that tall stops reading as an accent and starts reading as the
 * background, which left the active row competing with the rail it sat on for
 * attention. It also fused two unrelated decisions: because the rail WAS the
 * primary token, "make the menu darker" and "change the brand" were the same
 * edit, and neither could be done without doing the other.
 *
 * So the rail now owns three tokens of its own (see apps/web/src/index.css):
 *   --nav-surface     the rail. INK IN BOTH THEMES — it does not invert.
 *   --nav-on-surface  every mark on it; the alphas below step the hierarchy.
 *   --nav-active      the brand, lifted until it reads on ink. Used ONLY to say
 *                     WHERE YOU ARE — the current row's edge bar and its glyph —
 *                     plus the rail's one primary button.
 *
 * `--nav-active` is deliberately not `--primary`: at #5323e6 the brand is far
 * too dark against #1c1a25 to mark anything. It is the same hue lifted, and it
 * lifts from the new seed whenever the brand changes.
 *
 * Because the rail is ink in both themes, one set of alphas is correct in both.
 * Measured against the rail (composited, WCAG 2.1 relative luminance):
 *
 *   active label, full opacity ......... 17.16:1 light / 19.29:1 dark
 *   resting nav label /80 .............. 11.29:1 / 12.36:1
 *   group eyebrow /60 ..................  6.89:1 /  7.18:1
 *   --nav-active bar AND glyph ..........  5.87:1 /  6.59:1  (needs 3:1)
 *
 * The group eyebrow moved /75 → /60 in the warm-paper reskin, and it is the one
 * step here that got QUIETER on purpose. A section heading and a destination are
 * different kinds of thing — one is a place you can go, the other is the name of
 * what is under it — and at /75 against rows at /80 they were the same voice, so
 * the only thing separating them was the 12px caps. /60 is still 6.89:1, i.e.
 * comfortably above the 4.5:1 these headings need as interactive text, and it is
 * dimmer than every row it introduces.
 *
 *
 * Those are all against the BARE rail, which is the wrong ground for the one row
 * that matters. The current row and a hovered row each sit on a ground of their
 * own, and since 2026-09-05 those grounds carry the BRAND:
 *
 *   --nav-hover-surface   the brand mixed into the rail at 10%
 *   --nav-active-surface  the same mix at 20%
 *
 * They replace a single `bg-nav-on-surface/10` that was doing both jobs — one
 * neutral grey rectangle answering "where am I" and "what is under the pointer"
 * identically, in nobody's brand. The owner's ask was that both follow the
 * brand, and the mix is what makes that true without a hue being written down
 * twice: both grounds are `--nav-active`, which is `--primary` lifted until it
 * reads on ink, so the rail's hover and current row are the SAME ACCENT as the
 * primary button on the page beside them. (They briefly were not: the branding
 * store re-seeded `--nav-active` alone from the tenant's hex, and a rail in one
 * hue beside a button in another is what the owner reported. See the note in
 * stores/brandingStore.ts.) Full reasoning and every measurement, including the
 * sweep across every brand this token can be re-seeded to, is in index.css
 * beside the declarations.
 *
 * Re-measured over the current row's new ground (light / dark):
 *
 *   active label, full opacity ......... 13.49:1 / 15.00:1
 *   --nav-active bar AND glyph ..........  4.31:1 /  5.13:1  (needs 3:1)
 *
 * — so the label clears AA text (4.5:1) and the brand marks clear the 3:1
 * non-text floor in both themes, with the light rail the binding case. The row
 * also lifts off the rail marginally harder than the wash it replaces (1.37:1
 * against 1.34:1), so nothing was traded away for the hue.
 *
 * `--nav-border` is structural (dividers), not text, so it answers to the 3:1
 * non-text rule or to nothing at all.
 *
 * `--ring` is `--primary`, which measures 2.26:1 on this rail in light theme —
 * so `RAIL_FOCUS` draws in `--nav-active` instead. That sentence used to say
 * `ring-inset` + `ring-nav-on-surface`, and the code had said `outline-ring`
 * for some time: the note was right about the problem and stale about the fix,
 * which is how a 2.26:1 focus ring survives review. See railSkin.ts.
 * ======================================================================== */

interface SidebarProps {
  /** DOM id — the header's menu button points its `aria-controls` here. */
  id?: string;
  /** Mobile drawer state. Ignored at md+, where the rail is always in flow. */
  open?: boolean;
  /** Supplied only while the mobile drawer is open; renders its close button. */
  onClose?: () => void;
}

export function Sidebar({ id, open = false, onClose }: SidebarProps): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  // A login with no linked Employee record (a pure admin/service account) has
  // no attendance, no leave balance and no profile — every "My …" destination
  // dead-ends with an empty state. The feature flag says the DEPLOYMENT offers
  // self-service; this says THIS USER can actually use it.
  const hasEmployeeRecord = useAuthStore((s) => Boolean(s.user?.employeeId));
  const branding = useBrandingStore((s) => s.branding);
  const features = useFeatures();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(loadCollapsed);
  // Names the personal list for a screen reader — see `SectionHeading`.
  const personalHeadingId = useId();

  // Every personal destination the tenant runs, Home included. Home used to be
  // dropped whenever the check-in CTA rendered, because both point at `/`; the
  // CTA is gone from the rail (see the note at its old site), so the row it was
  // shadowing comes back and the rail matches §2's PERSONAL list.
  const personalItems = hasEmployeeRecord
    ? PERSONAL.filter((i) => featureOn(i, features))
    : [];
  const visibleGroups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => isVisible(i, role, features)),
  })).filter((g) => g.items.length > 0);

  // Which surface this is, and whether it is the phone-shaped one. Decided in
  // `shellSurface.ts` because AppShell and MobileBottomNav need the same answer.
  const { mode, canAdmin, phoneShaped } = useShellSurface();

  // Highlight only the MOST-SPECIFIC matching item, so a parent (e.g. /employees,
  // /reports) doesn't stay lit while you're on a child route (/employees/import,
  // /reports/coverage). The longest matching `to` among the visible items wins.
  const activeTo = [
    ...personalItems.map((i) => i.to),
    ...TOP_LEVEL.map((i) => i.to),
    ...visibleGroups.flatMap((g) => g.items.map((i) => i.to)),
  ]
    .filter((i) => pathMatches(i, pathname))
    .reduce<string | undefined>(
      (best, to) => (best && best.length >= to.length ? best : to),
      undefined
    );

  function toggleCollapsed(): void {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — keep in-memory only */
      }
      return next;
    });
  }

  /*
   * GROUPS OPEN BY DEFAULT — a chevron is not a place to hide the app.
   *
   * They used to default to "open only if the active route is inside me",
   * which meant a cold load into the admin landing had NOTHING active and
   * therefore every section shut. Measured from that state: leave approvals
   * four clicks, add an employee four, a named report five — three of them
   * paying a click purely to reveal a heading's children.
   *
   * The stored preference still wins in both directions, so anyone who prefers
   * a short rail collapses them once and keeps it.
   */

  const [expanded, setExpanded] = useState<Record<string, boolean>>(loadExpanded);
  /* Which group owns the current route — used to force it open above. */
  const activeGroupId = visibleGroups.find((g) =>
    g.items.some((i) => pathMatches(i.to, pathname))
  )?.id;

  function toggleGroup(groupId: string): void {
    setExpanded((prev) => {
      const isOpen = groupId in prev ? prev[groupId]! : true;
      const next = { ...prev, [groupId]: !isOpen };
      try {
        localStorage.setItem(NAV_EXPANDED_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable — keep the choice in memory for this visit */
      }
      return next;
    });
  }
  const groups = visibleGroups.map((g) => (
    <NavGroupSection
      key={g.id}
      group={g}
      activeTo={activeTo}
      collapsed={collapsed}
      /* THE GROUP YOU ARE STANDING IN IS ALWAYS OPEN, whatever the stored
         preference says — and that is the whole reason this feature could come
         back. It was removed because a shut group STAYED shut across sessions,
         so a reader returned to a rail missing rows the design never hides, with
         no clue the page they were on lived inside one of them. Forcing the
         active group open closes that: you can be looking at Corrections and
         have Approvals shut, which is the state that stranded people.
         It is not written back to storage — the reader's own choice is left
         intact for when they navigate away again. */
      open={g.id === activeGroupId || (g.id in expanded ? expanded[g.id]! : true)}
      onToggle={() => toggleGroup(g.id)}
    />
  ));

  return (
    <nav
      id={id}
      aria-label={t('appShell.mainNav', 'Main navigation')}
      className={cn(
        'flex h-full shrink-0 flex-col overflow-hidden bg-nav-surface text-nav-on-surface',
        'w-sidebar',
        collapsed && 'md:w-sidebar-collapsed',
        // OFF-CANVAS ONLY ON THE PHONE-SHAPED SURFACE. Below md the worker's
        // rail is an overlay anchored to the INLINE start edge, so it slides in
        // from the left in English and the right in Arabic, and never squeezes
        // the content. The BACK OFFICE stays a docked column at every width —
        // see `shellSurface.ts` for why — so none of this applies there.
        //
        // `invisible` (rather than unmounting) keeps the closed drawer out of
        // the tab order and the accessibility tree; because `visibility` is in
        // the transition list it holds `visible` for the whole slide-OUT and
        // flips the moment the slide-in starts, so both directions animate.
        // `shadow-popover` is the ramp's "floating over the page" weight: it is
        // the only edge saying where the drawer stops and the page it covers
        // begins. Scoped to `max-md:` because at md and up the rail is a column
        // in the layout, and a docked column with a drop shadow is a card
        // pretending to be furniture.
        phoneShaped &&
          'max-md:fixed max-md:inset-y-0 max-md:start-0 max-md:z-50 max-md:shadow-popover',
        phoneShaped &&
          !open &&
          'max-md:invisible max-md:-translate-x-full max-md:rtl:translate-x-full',
        'transition-[width,transform,visibility] duration-normal ease-standard'
      )}
    >
      {/* Brand + rail controls. At the top bar's own height so the rail head and
          that bar read as one horizontal band — which matters more now than it
          did, because the top bar is STICKY: the band is permanent, so a rail
          head that disagreed with it by a few pixels would disagree forever
          rather than only until someone scrolled.

          `min-h-header` rather than `h-header`, for the same reason the bar
          itself takes a minimum: a tenant name is of unknown length and a fixed
          height clips it. */}
      <div
        className={cn(
          // NO HAIRLINE, AND NO 56px FLOOR. Both were an attempt to line this
          // row up with the content column's top bar, and the handoff does not
          // make that alignment: its rail is a full-height grid column with its
          // own `padding: 18px 14px 14px`, so the logo sits 18px from the top
          // while the top bar next to it is 56px tall, and no rule separates
          // the head from the rows (`Hadir Dashboard.dc.html:32-41`). The floor
          // made the head 22px taller than the design and the border drew a
          // line across the rail that the mock has nowhere.
          'flex shrink-0 items-center gap-2.5 pt-3.5',
          RAIL_INSET,
          RAIL_DIVIDER,
          collapsed && cn('md:justify-center', RAIL_INSET_COLLAPSED)
        )}
      >
        {/* `md` (36px), not `sm` (28px) — the mock's rail tile is 34px, and
            `md` is the size step that actually lands near it; `sm` was
            visibly undersized against the wordmark beside it. */}
        <span className={cn('flex shrink-0 items-center', collapsed && 'md:hidden')}>
          <BrandLogo size="md" fallback="monogram" />
        </span>
        {/* The wordmark's own face: Archivo, bold, the display steps' tight
            tracking — `Hadir Dashboard.dc.html` sets this row at
            16px/700/−0.2px, and `text-h3` (17px/600/−0.3px) is the closest
            registered step to it rather than a one-off arbitrary value; the
            weight is pushed the rest of the way to 700 with `font-bold`. It
            was `text-base font-semibold` (16px/600, the UI-text face, no
            tracking) — the app's own name in the app's own body font. */}
        <span
          className={cn(
            'flex-1 truncate font-display text-h3 font-bold text-nav-on-surface',
            collapsed && 'md:hidden'
          )}
        >
          {branding?.appName ?? t('app.name', 'Attendance')}
        </span>

        {/* Collapse toggle — desktop only; the drawer is always full width.
            
            `IconButton`, not a `<button>` wearing a class bundle. It was the
            latter, and §6 is explicit that a hand-rolled control is a finding
            rather than a design: the app has exactly one icon-only control and
            this is one. Importing it costs nothing the old spelling had and
            adds what it was missing — a press state, a declared square size
            instead of one the glyph happened to produce, and a `label` the
            component will not render without.
            
            The state announcement is UNCHANGED and is the reason `label` is
            computed rather than fixed: `IconButton` makes `label` both the
            accessible name and the tooltip, so "Collapse navigation" /
            "Expand navigation" still flips with the rail, and `aria-expanded`
            still carries the machine-readable half. */}
        <IconButton
          size="sm"
          onClick={toggleCollapsed}
          className={cn('hidden md:flex', RAIL_ICON_BUTTON)}
          label={
            collapsed
              ? t('appShell.expandNav', 'Expand navigation')
              : t('appShell.collapseNav', 'Collapse navigation')
          }
          aria-expanded={!collapsed}
        >
          {/* Points at the edge the rail moves toward. `rtl:-scale-x-100`
              mirrors it so it still points outward in Arabic. */}
          <ChevronLeft
            {...NAV_ICON}
            className={cn('rtl:-scale-x-100', collapsed && 'rotate-180')}
            aria-hidden
          />
        </IconButton>

        {/* Close — mobile only, and only while the drawer is actually open.
            The same primitive: two controls in one rail head drawn two ways is
            how the second control language starts. */}
        {onClose && (
          <IconButton
            size="sm"
            onClick={onClose}
            className={cn('flex md:hidden', RAIL_ICON_BUTTON)}
            label={t('appShell.closeMenu', 'Close menu')}
          >
            <X {...NAV_ICON} aria-hidden />
          </IconButton>
        )}
      </div>

      {/* The rail's scrolling body. It owns the inline inset for everything
          inside it (see RAIL_INSET) — rows, the pills, the group hairlines — so
          "how far in does a row start" has one answer instead of six. */}
      <div
        className={cn(
          // `pt-[22px]` is the handoff's gap between the rail head and what
          // follows it (`gap: 22px` on the nav), and `pb-3.5` its 14px bottom
          // padding. This was `py-2` — 8px — which is why the rail read as a
          // packed list where the mock reads as three separated blocks.
          'flex-1 overflow-y-auto overflow-x-hidden pt-rail-gap pb-3.5',
          RAIL_INSET,
          collapsed && RAIL_INSET_COLLAPSED
        )}
      >
        {mode === 'admin' ? (
          <>
            {/* Leave the Admin panel, back to the personal workspace. */}
            <SurfaceSwitch
              to="/"
              icon={ArrowLeft}
              mirrorRtl
              label={t('nav.backToWorkspace', 'Back to my workspace')}
              collapsed={collapsed}
            />

            {/* Two top-level destinations, then the back-office sections:
                the summary, and today's floor. Operations is not in a group
                because it IS the group — one screen replacing what used to be
                a three-item "Operations" section. */}
            {/* The destinations that stand on their own, above the sections:
                the summary, today's floor, the month, the archive. Each owns an
                internal navigator of its own, which is exactly why none of them
                belongs inside a collapsible list of siblings — see TOP_LEVEL. */}
            {/* `gap-0.5` — the handoff sets 2px between rows inside a group
                (`gap: 2px`), so the pills read as a stack rather than a solid
                block. They were flush against each other. */}
            {/* `pt-[22px]`, the handoff's gap between the surface switch and
                the first group of destinations (`gap: 22px` on the nav column,
                `Hadir Dashboard.dc.html:32`). It was `pt-1` — 4px — so the
                back-pill and the rows below it read as one run rather than as
                two blocks. */}
            <ul className="flex flex-col gap-0.5 pt-rail-gap">
              {TOP_LEVEL.filter((i) => isVisible(i, role, features)).map((item) => (
                <li key={item.to}>
                  <Leaf item={item} activeTo={activeTo} collapsed={collapsed} />
                </li>
              ))}
            </ul>

            {groups}
          </>
        ) : (
          <>
            {/* NO CHECK-IN CTA IN THE RAIL, and its absence is the design.
                
                A brand-filled "Check in / out" pill used to sit here, above the
                personal rows, pointing at `/` — and because Home also points at
                `/`, Home was filtered out to avoid two rows for one
                destination. So the rail's first item was a button where the
                design has a nav row.
                
                Both authorities say otherwise. §2 of docs/design-rules.md lists
                PERSONAL as "Home, My attendance, Corrections, My leave,
                Announcements, Letters, My profile" — a Home row, no CTA — and
                `Hadir Check-in Desktop.dc.html:47-56` draws exactly that, with
                Home carrying the active marker like any other row. The CTA also
                rendered its ACTIVE state as a translucent white wash, so on the
                one screen it points at, the app's primary action turned into a
                grey slab.
                
                The primary action is not lost. §2 puts it in the bottom third
                ON THE PHONE, which is where `MobileBottomNav` already raises
                it, and `PRIMARY` still feeds `allDestinations` so ⌘K reaches
                "Check in / out" by name. What changed is that the DESKTOP rail
                is a list of places again, and Home is one of them. */}
            {/* Personal — under its own heading, so on a COMBINED console the
                worker's own rows are a named section rather than a run of links
                that happens to stop where the first admin group starts. Nothing
                is drawn when the list is empty: an admin/service login with no
                Employee record behind it has no personal rows, and a heading
                over nothing is a section that does not exist. */}
            {personalItems.length > 0 && (
              <>
                <SectionHeading
                  headingId={personalHeadingId}
                  icon={PERSONAL_SECTION.icon}
                  label={t(PERSONAL_SECTION.titleKey, PERSONAL_SECTION.title)}
                  collapsed={collapsed}
                />
                <ul className="flex flex-col gap-0.5" aria-labelledby={personalHeadingId}>
                  {personalItems.map((item) => (
                    <li key={item.to}>
                      <Leaf item={item} activeTo={activeTo} collapsed={collapsed} />
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* On a management-only console the groups sit inline (no split);
                otherwise a user with management rights gets a switch INTO the
                Admin panel, and the back-office lives entirely over there. */}
            {mode === 'combined'
              ? groups
              : canAdmin && (
                  <div className="pt-2">
                    <SurfaceSwitch
                      to={ADMIN_HOME}
                      icon={ShieldCheck}
                      label={t('nav.adminPanel', 'Admin panel')}
                      collapsed={collapsed}
                    />
                  </div>
                )}
          </>
        )}
      </div>

      {/* Build stamp — dev metadata, and the first thing to go when the rail is
          only 52px wide. Hidden with `md:hidden` so the mobile drawer, which is
          never collapsed, keeps it. */}
      <div className={cn('shrink-0', collapsed && 'md:hidden')}>
        <AppVersion />
      </div>
    </nav>
  );
}
